import { useMutation, useQuery } from '@apollo/client'
import { ALL_TIME_ADDONS } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { OldAddButton, Header } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import { format, formatISO, parseISO } from 'date-fns'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { OverrideOrAddonForm } from './types'
import { EditAddonPrompt } from './EditAddonPrompt'
import type { TimeAddonNode } from './types.graphql'
import {
  CREATE_TIME_ADDON,
  DELETE_TIME_ADDON,
  PATCH_TIME_ADDON,
} from './mutations'
import { PERMISSIONS } from '@/utils/permissions'

const Wrapper = styled(Card)`
  position: static;
  margin: 1rem 0;
`

const H2 = styled.h2`
  display: flex;

  margin: 1rem 0 0 8px;
  font-weight: 600;

  div {
    margin-left: 1rem;
  }
`

interface TimeAddonsProps {
  datetimeStart: Date
  datetimeEnd: Date
  userId: string
  onChange?: () => void
}

export const TimeAddons: React.JSX.CFC<TimeAddonsProps> = ({
  datetimeStart,
  datetimeEnd,
  userId,
  onChange,
}) => {
  const translations = useTranslate({
    timeAddons: 'salary.time-addons',
    overrideOtherAddons: 'salary.override-other-addons',

    start: 'common.start',
    end: 'common.end',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteTimeOverride: 'salary.delete-time-addon',
    deleteTimeOverrideConfirm: 'salary.delete-time-addon-confirm',
    comment: 'common.comment',

    toast: {
      successCreate: 'salary.time-addon-create-success',
      successUpdate: 'salary.time-addon-update-success',
      successDelete: 'salary.time-addon-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_TIME_ADDONS, {
    variables: {
      datetimeGte: formatISO(datetimeStart),
      user: userId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_TIME_ADDONS,
      variables: {
        datetimeGte: formatISO(datetimeStart),
        user: userId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createTimeAddon] = useMutation(CREATE_TIME_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
      onChange?.()
    },
  })

  const [patchTimeAddon] = useMutation(PATCH_TIME_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
      onChange?.()
    },
  })

  const [deleteTimeAddon] = useMutation(DELETE_TIME_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
      onChange?.()
    },
  })

  const timeAddons = useNodes(data?.allTimeAddons)

  async function handleAdd() {
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={{
          datetimeStart,
          datetimeEnd,
          salaryCode: null,
          overrideOtherAddons: false,
          comment: '',
        }}
        userId={userId}
        onSubmit={resolve}
      />
    ))

    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return

    void createTimeAddon({
      variables: {
        input: {
          user: userId,
          datetimeStart: formatISO(data.datetimeStart),
          datetimeEnd: formatISO(data.datetimeEnd),
          salaryCode: data.salaryCode,
          overrideOtherAddons: data.overrideOtherAddons,
          comment: data.comment,
        },
      },
    })
  }

  async function handleEdit(addon: TimeAddonNode) {
    const initialData = {
      datetimeStart: parseISO(addon.datetimeStart),
      datetimeEnd: parseISO(addon.datetimeEnd),
      salaryCode: addon.salaryCode?.id ?? '',
      overrideOtherAddons: addon.overrideOtherAddons ?? false,
      comment: addon.comment ?? '',
    }
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={initialData}
        userId={userId}
        onSubmit={resolve}
        addonId={addon.id}
      />
    ))

    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return

    void patchTimeAddon({
      variables: {
        id: addon.id,
        input: {
          datetimeStart: formatISO(data.datetimeStart),
          datetimeEnd: formatISO(data.datetimeEnd),
          salaryCode: data.salaryCode,
          overrideOtherAddons: data.overrideOtherAddons,
          comment: data.comment,
        },
      },
    })
  }

  async function handleDelete(addon: TimeAddonNode) {
    const { data } = await confirm(
      translations.deleteTimeOverrideConfirm,
      translations.deleteTimeOverride
    )

    if (!data) return

    void deleteTimeAddon({
      variables: {
        id: addon.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <Header>
        <H2>{translations.timeAddons}</H2>
        {loading && <Loader.Spinner />}
        {!loading && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </Header>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.start}</th>
              <th>{translations.end}</th>
              <th>{translations.salaryCode}</th>
              <th>{translations.overrideOtherAddons}</th>
              <th>{translations.comment}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>

          <tbody>
            {timeAddons.map(timeAddon => (
              <tr key={timeAddon.id}>
                <td>
                  {format(parseISO(timeAddon.datetimeStart), 'dd. MMM HH:mm')}
                </td>
                <td>
                  {format(parseISO(timeAddon.datetimeEnd), 'dd. MMM HH:mm')}
                </td>
                <td>
                  {timeAddon.salaryCode ? timeAddon.salaryCode.name : '-'}
                </td>
                <td>
                  {timeAddon.overrideOtherAddons !== null ? (
                    <div>
                      <Icon
                        icon={timeAddon.overrideOtherAddons ? 'check' : 'times'}
                        type="light"
                        color={
                          timeAddon.overrideOtherAddons ? 'success' : 'red'
                        }
                      />
                    </div>
                  ) : (
                    <div></div>
                  )}
                </td>
                <td>{timeAddon.comment ? timeAddon.comment : '-'}</td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(timeAddon)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(timeAddon)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
